<script setup>
import Layout from "../../../layouts/main.vue";
import mushroom from "cem-primary-api";
import { useRoute, useRouter } from "vue-router";
import { onMounted, reactive, ref, watch, computed } from "vue";
import { FormInstance } from "element-plus";
import toastr from "toastr";
import { useI18n } from "vue-i18n";
import Swal from "sweetalert2";
import { Filter, Project } from "mushroomjs";
import { langWeb } from "../../../stores/lang";
const lang_web = langWeb();
const lang = ref(lang_web.showLang.toLocaleUpperCase());

// data khai báo
import MethodService from "../../../service/MethodService";
import Data from "./dataFormSearchFilter";
const { t } = useI18n();
const ruleFormRef = ref(FormInstance);
const dataForm = reactive({
  value: MethodService.copyObject(Data.dataForm),
});
const loadingBtn = ref(false);
const validateFlag = ref(false);
const strategyOptions = ref([]);
const sourceJsonText = ref("");

const router = useRoute();
const routerPush = useRouter();
const isUpdateMode = ref(false);
const fromStrategyPage = ref(false);

// validate form
import Validate from "../../../service/ValidService";
import LanguageService from "../../../service/LanguageService";

const validateWidth = (rule, value, callback) => {
  if (value !== undefined && value !== null && value !== "") {
    if (value <= 0) {
      callback(new Error(t("t-width-must-be-greater-than-zero")));
    } else {
      callback();
    }
  } else {
    callback();
  }
};

const rulesForm = reactive({
  code: [Validate.required],
  "name.vi": [Validate.required],
  "name.en": [Validate.required],
  input_type: [Validate.requiredChange],
  search_strategy_id: [Validate.requiredChange],
  width: [{ validator: validateWidth, trigger: "blur" }],
});

// hiển thị source config khi input_type = dropdown
const showSourceConfig = computed(() => {
  return dataForm.value.input_type === "dropdown";
});

// chuyển text nhập theo dòng thành JSON string
const convertTextToJson = (text) => {
  if (!text || !text.trim()) return "";
  const lines = text
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line !== "");
  return JSON.stringify(lines);
};

// chuyển JSON string thành text theo dòng (dùng khi load dữ liệu cập nhật)
const convertJsonToText = (jsonStr) => {
  if (!jsonStr || !jsonStr.trim()) return "";
  try {
    const arr = JSON.parse(jsonStr);
    if (Array.isArray(arr)) {
      return arr
        .map((item) => {
          if (typeof item === "string") return item;
          return item.value || item.label || "";
        })
        .join("\n");
    }
  } catch (e) {
    // Nếu không parse được JSON, trả về nguyên text
    return jsonStr;
  }
  return jsonStr;
};

// lấy danh sách hướng tìm kiếm
const getStrategyOptions = async () => {
  try {
    const response = await mushroom.search_strategy.getAllAsync({
      fields: Project.include("id", "name").build(),
    });
    if (response.result) {
      strategyOptions.value = response.result.map((item) => ({
        value: item.id,
        label: item.name?.vi || item.name?.en || item.id,
      }));
    }
  } catch (e) {
    console.error("Có lỗi: %o", e);
  }
};

// kiểm tra code đã tồn tại ở hướng tìm kiếm khác chưa
const checkCodeExists = async (code) => {
  try {
    const response = await mushroom.search_filter.getAllAsync({
      filters: Filter.eq("code", code).build(),
      fields: Project.include("id", "code", "search_strategy_id").build(),
    });
    if (response.result && response.result.length > 0) {
      // lọc ra những filter có strategy khác với strategy hiện tại
      const currentStrategyId = dataForm.value.search_strategy_id;
      const currentId = router.params.id;
      const existsInOther = response.result.filter(
        (item) =>
          item.id !== currentId &&
          item.search_strategy_id !== currentStrategyId,
      );
      return existsInOther.length > 0;
    }
    return false;
  } catch (e) {
    console.error("Có lỗi: %o", e);
    return false;
  }
};

const buildObjectData = (isUpdate = false) => {
  const objectData = {
    code: dataForm.value.code.trim(),
    name: {
      vi: dataForm.value.name.vi.trim(),
      en: dataForm.value.name.en.trim(),
    },
    input_type: dataForm.value.input_type,
  };
  // API không cho phép cập nhật search_strategy_id, chỉ gửi khi thêm mới
  if (!isUpdate) {
    objectData.search_strategy_id = dataForm.value.search_strategy_id || null;
  }
  if (
    dataForm.value.default_value_label?.vi ||
    dataForm.value.default_value_label?.en
  ) {
    objectData.default_value_label = {
      vi: dataForm.value.default_value_label?.vi || "",
      en: dataForm.value.default_value_label?.en || "",
    };
  }
  if (dataForm.value.input_type === "dropdown" && dataForm.value.source?.from) {
    objectData.source = {
      from: dataForm.value.source.from,
    };
    if (dataForm.value.source.from === "json") {
      objectData.source.json = convertTextToJson(sourceJsonText.value);
    }
    if (dataForm.value.source.from === "sql") {
      objectData.source.sql = dataForm.value.source.sql || "";
    }
  }
  if (
    dataForm.value.index !== undefined &&
    dataForm.value.index !== null &&
    dataForm.value.index !== ""
  ) {
    objectData.index = Number(dataForm.value.index);
  }
  if (
    dataForm.value.width !== undefined &&
    dataForm.value.width !== null &&
    dataForm.value.width !== ""
  ) {
    objectData.width = Number(dataForm.value.width);
  } else {
    objectData.width = null;
  }
  objectData.tooltip =
    dataForm.value.tooltip && dataForm.value.tooltip.trim()
      ? dataForm.value.tooltip.trim()
      : null;
  return objectData;
};

// thêm mới
const addNew = (formEl) => {
  validateFlag.value = true;
  if (!formEl) return;
  formEl.validate(async (valid) => {
    if (!valid) return;
    loadingBtn.value = true;
    try {
      // kiểm tra code tồn tại ở hướng tìm kiếm khác
      const codeExists = await checkCodeExists(dataForm.value.code.trim());
      if (codeExists) {
        loadingBtn.value = false;
        const confirmResult = await Swal.fire({
          title: t("t_warning"),
          text: t("t-search-filter-code-exists-warning"),
          icon: "warning",
          confirmButtonColor: "#556ee6",
          confirmButtonText: t("swal_accept"),
          cancelButtonText: t("swal_cancel"),
          showCancelButton: true,
          allowOutsideClick: false,
        });
        if (!confirmResult.isConfirmed) return;
        loadingBtn.value = true;
      }

      const objectData = buildObjectData();
      const newId = await mushroom.search_filter.createAsync(objectData);
      if (newId) {
        toastr.success(t("toastr_add_success"));
      }
      routerPush.go(-1);
    } catch (error) {
      if (error?.meta?.field === "$unique_key") {
        toastr.error(t("toastr_name_existed"));
      } else {
        console.error("Có lỗi: %o", error);
        MethodService.showError(error.code);
      }
    } finally {
      loadingBtn.value = false;
    }
  });
};

// cập nhật
const update = (formEl) => {
  validateFlag.value = true;
  if (!formEl) return;
  formEl.validate(async (valid) => {
    if (!valid) return;
    loadingBtn.value = true;
    try {
      // kiểm tra code tồn tại ở hướng tìm kiếm khác
      const codeExists = await checkCodeExists(dataForm.value.code.trim());
      if (codeExists) {
        loadingBtn.value = false;
        const confirmResult = await Swal.fire({
          title: t("t_warning"),
          text: t("t-search-filter-code-exists-warning"),
          icon: "warning",
          confirmButtonColor: "#556ee6",
          confirmButtonText: t("swal_accept"),
          cancelButtonText: t("swal_cancel"),
          showCancelButton: true,
          allowOutsideClick: false,
        });
        if (!confirmResult.isConfirmed) return;
        loadingBtn.value = true;
      }

      const objectData = buildObjectData(true);
      objectData.id = router.params.id || dataForm.value.id;
      const response = await mushroom.search_filter.partialUpdateAsync(
        objectData,
      );
      if (response.result === (router.params.id || dataForm.value.id)) {
        toastr.success(t("toastr_update_success"));
        routerPush.go(-1);
      }
    } catch (error) {
      if (error?.meta?.field === "$unique_key") {
        toastr.error(t("toastr_name_existed"));
      } else {
        console.error("Có lỗi: %o", error);
        MethodService.showError(error.code);
      }
    } finally {
      loadingBtn.value = false;
    }
  });
};

// lấy thông tin chi tiết
const getDetailById = async (idSearchFilter) => {
  if (!idSearchFilter) return;
  mushroom.search_filter.invalidCache();
  try {
    const response = await mushroom.search_filter.findByIdAsync({
      id: idSearchFilter,
      fields:
        "id,search_strategy_id,code,name,input_type,default_value_label,source,index,width,tooltip",
    });
    const result = response.result;
    if (!result.default_value_label) {
      result.default_value_label = { vi: "", en: "" };
    }
    if (!result.source) {
      result.source = { from: undefined, json: "", sql: "" };
    }
    dataForm.value = result;
    // Chuyển JSON string thành text theo dòng để hiển thị
    if (result.source?.json) {
      sourceJsonText.value = convertJsonToText(result.source.json);
    }
  } catch (e) {
    console.error("Có lỗi: %o", e);
    MethodService.showError(e.code);
  }
};

watch(
  () => lang_web.showLang,
  () => {
    lang.value = lang_web.showLang.toLocaleUpperCase();
    LanguageService.lang = LanguageService[lang.value];
    if (validateFlag.value)
      rulesForm.value = MethodService.copyObject(rulesForm);
  },
);

onMounted(async () => {
  await getStrategyOptions();
  const queryStrategyId = router.query?.search_strategy_id;
  const searchFilterId = router.params?.id;

  if (searchFilterId) {
    // Cập nhật: lấy dữ liệu theo id trên URL
    isUpdateMode.value = true;
    getDetailById(searchFilterId);
  } else {
    // Thêm mới
    isUpdateMode.value = false;
    if (queryStrategyId) {
      // Có search_strategy_id trên URL → tự điền và disabled
      fromStrategyPage.value = true;
      dataForm.value.search_strategy_id = queryStrategyId;
    }
  }
});
</script>

<template>
  <Layout>
    <div class="card">
      <div class="card-header">
        <h3 v-if="!isUpdateMode">
          {{ $t("t-add-search-filter") }}
        </h3>
        <h3 v-else>{{ $t("t-update-search-filter") }}</h3>
      </div>
      <div class="card-body">
        <div class="main-body">
          <el-form
            ref="ruleFormRef"
            :model="dataForm.value"
            status-icon
            :rules="rulesForm"
            label-width="200px"
            class="form-user"
          >
            <!-- Hướng tìm kiếm -->
            <el-form-item
              :label="$t('t-search-filter-strategy')"
              prop="search_strategy_id"
            >
              <el-select
                v-model="dataForm.value.search_strategy_id"
                filterable
                clearable
                :disabled="fromStrategyPage || isUpdateMode"
                :placeholder="$t('t-place-search-filter-strategy')"
                style="width: 100%"
                :no-match-text="$t('t-match-data')"
                :no-data-text="$t('t-no-data')"
              >
                <el-option
                  v-for="item in strategyOptions"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
                />
              </el-select>
            </el-form-item>

            <!-- Mã tiêu chí -->
            <el-form-item prop="code">
              <template #label>
                <div style="display: inline-flex; align-items: center; gap: 4px;">
                  <span>{{ $t('t-search-filter-code') }}</span>
                  <el-tooltip
                    effect="dark"
                    placement="top"
                    popper-class="tooltip-code-restricted"
                    :content="$t('t-search-filter-restricted-codes-tooltip')"
                  >
                    <i class="ri-information-line" style="cursor: pointer; font-size: 14px; color: var(--el-color-info);"></i>
                  </el-tooltip>
                </div>
              </template>
              <el-input
                type="text"
                autocomplete="off"
                v-model="dataForm.value.code"
                :disabled="isUpdateMode"
                @change="dataForm.value.code = dataForm.value.code.trim()"
                :placeholder="$t('t-place-search-filter-code')"
              />
            </el-form-item>

            <!-- Tên tiếng Việt -->
            <el-form-item :label="$t('t-search-filter-name-vi')" prop="name.vi">
              <el-input
                type="text"
                autocomplete="off"
                v-model="dataForm.value.name.vi"
                @change="dataForm.value.name.vi = dataForm.value.name.vi.trim()"
                :placeholder="$t('t-place-search-filter-name-vi')"
              />
            </el-form-item>

            <!-- Tên tiếng Anh -->
            <el-form-item :label="$t('t-search-filter-name-en')" prop="name.en">
              <el-input
                type="text"
                autocomplete="off"
                v-model="dataForm.value.name.en"
                @change="dataForm.value.name.en = dataForm.value.name.en.trim()"
                :placeholder="$t('t-place-search-filter-name-en')"
              />
            </el-form-item>

            <!-- Tooltip -->
            <el-form-item
              :label="$t('t-search-filter-tooltip')"
              prop="tooltip"
            >
              <el-input
                type="textarea"
                autocomplete="off"
                v-model="dataForm.value.tooltip"
                :placeholder="$t('t-place-search-filter-tooltip')"
              />
            </el-form-item>

            <!-- Loại nhập liệu + Thứ tự + Độ rộng -->
            <b-row>
              <b-col md="4">
                <el-form-item
                  :label="$t('t-search-filter-input-type')"
                  prop="input_type"
                >
                  <el-select
                    v-model="dataForm.value.input_type"
                    :placeholder="$t('t-place-search-filter-input-type')"
                    style="width: 100%"
                    :no-match-text="$t('t-match-data')"
                    :no-data-text="$t('t-no-data')"
                  >
                    <el-option
                      v-for="item in Data.inputTypeOptions"
                      :key="item.value"
                      :label="$t(item.labelKey)"
                      :value="item.value"
                    />
                  </el-select>
                </el-form-item>
              </b-col>
              <b-col md="4">
                <el-form-item :label="$t('t-search-filter-index')" prop="index">
                  <el-input-number
                    v-model="dataForm.value.index"
                    :min="1"
                    :step="1"
                    :precision="0"
                    :placeholder="$t('t-place-search-filter-index')"
                    controls-position="right"
                    style="width: 100%"
                  />
                </el-form-item>
              </b-col>
              <b-col md="4">
                <el-form-item :label="$t('t-search-filter-width')" prop="width">
                  <el-input-number
                    v-model="dataForm.value.width"
                    :min="1"
                    :step="1"
                    :precision="0"
                    :value-on-clear="null"
                    :placeholder="$t('t-place-search-filter-width')"
                    controls-position="right"
                    style="width: calc(100% - 30px)"
                  />
                  <span class="width-unit">%</span>
                </el-form-item>
              </b-col>
            </b-row>

            <!-- Giá trị mặc định -->
            <b-row>
              <b-col md="6">
                <el-form-item
                  :label="$t('t-search-filter-default-value-vi')"
                  prop="default_value_label.vi"
                >
                  <el-input
                    type="text"
                    autocomplete="off"
                    v-model="dataForm.value.default_value_label.vi"
                    :placeholder="$t('t-place-search-filter-default-value-vi')"
                  />
                </el-form-item>
              </b-col>
              <b-col md="6">
                <el-form-item
                  :label="$t('t-search-filter-default-value-en')"
                  prop="default_value_label.en"
                >
                  <el-input
                    type="text"
                    autocomplete="off"
                    v-model="dataForm.value.default_value_label.en"
                    :placeholder="$t('t-place-search-filter-default-value-en')"
                  />
                </el-form-item>
              </b-col>
            </b-row>

            <!-- Cấu hình nguồn dữ liệu cho dropdown -->
            <template v-if="showSourceConfig">
              <el-divider>{{ $t("t-search-filter-source-config") }}</el-divider>

              <el-form-item
                :label="$t('t-search-filter-source-from')"
                prop="source.from"
              >
                <el-select
                  v-model="dataForm.value.source.from"
                  clearable
                  :placeholder="$t('t-place-search-filter-source-from')"
                  style="width: 100%"
                  :no-match-text="$t('t-match-data')"
                  :no-data-text="$t('t-no-data')"
                >
                  <el-option
                    v-for="item in Data.sourceFromOptions"
                    :key="item.value"
                    :label="$t(item.labelKey)"
                    :value="item.value"
                  />
                </el-select>
              </el-form-item>

              <el-form-item
                v-if="dataForm.value.source.from === 'json'"
                :label="$t('t-search-filter-source-json')"
                prop="source.json"
              >
                <el-input
                  type="textarea"
                  :rows="6"
                  autocomplete="off"
                  v-model="sourceJsonText"
                  :placeholder="$t('t-place-search-filter-source-json')"
                />
              </el-form-item>

              <el-form-item
                v-if="dataForm.value.source.from === 'sql'"
                :label="$t('t-search-filter-source-sql')"
                prop="source.sql"
              >
                <el-input
                  type="textarea"
                  :rows="6"
                  autocomplete="off"
                  v-model="dataForm.value.source.sql"
                  :placeholder="$t('t-place-search-filter-source-sql')"
                />
              </el-form-item>
            </template>

            <div class="text-center mt-3">
              <button
                type="button"
                class="btn btn-soft-secondary btn-border mx-3"
                @click="routerPush.go(-1)"
              >
                {{ $t("t-back") }}
              </button>
              <el-button
                type="button"
                style="height: 38px"
                class="btn btn-secondary btn-border"
                @click="addNew(ruleFormRef)"
                v-if="!isUpdateMode"
                :loading="loadingBtn"
              >
                {{ $t("t-add") }}
              </el-button>

              <el-button
                type="button"
                style="height: 38px"
                class="btn btn-secondary btn-border"
                @click="update(ruleFormRef)"
                :loading="loadingBtn"
                v-else
              >
                {{ $t("t-update") }}
              </el-button>
            </div>
          </el-form>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style scoped>
.width-unit {
  margin-left: 6px;
  font-size: 13px;
  color: #909399;
}
</style>
